/**
 * Header - Navigation bar with wallet connection.
 *
 * Modeled after the Subscrypts dApp Navbar:
 * - 3-column layout: Logo | Nav (center) | Wallet area (right)
 * - h-20 (80px) glass-panel
 * - Rounded-full pill nav links
 * - Network badge + balance pill + wallet button grouped
 * - Info button (circle-i)
 * - Responsive mobile hamburger menu
 */

import { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { Link, useLocation } from 'react-router-dom';
import { useWeb3 } from '../contexts/Web3Context';
import { useSubscryptsContract } from '../hooks/useSubscryptsContract';
import WalletButton from './WalletButton';

interface HeaderProps {
  /** When true, hides logo, nav links, and mobile menu. Shows only wallet + balance. */
  minimal?: boolean;
  /** Callback when info button is clicked */
  onInfoClick?: () => void;
}

const Header = ({ minimal = false, onInfoClick }: HeaderProps) => {
  const location = useLocation();
  const { account, isConnected, provider, getAbi } = useWeb3();
  const { balanceOf, formatTokenAmount, getDexAddresses } = useSubscryptsContract();

  const [subsBalance, setSubsBalance] = useState<string>('0');
  const [usdcBalance, setUsdcBalance] = useState<string>('0');
  const [ethBalance, setEthBalance] = useState<string>('0');
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Fetch all balances when connected
  useEffect(() => {
    const fetchBalances = async () => {
      if (!isConnected || !account) {
        setSubsBalance('0');
        setUsdcBalance('0');
        setEthBalance('0');
        return;
      }

      try {
        const subsBal = await balanceOf(account);
        setSubsBalance(formatTokenAmount(subsBal, 18));
      } catch (e) {
        console.error('Failed to fetch SUBS balance:', e);
      }

      try {
        if (provider) {
          const ethBal = await provider.getBalance(account);
          setEthBalance(parseFloat(ethers.formatEther(ethBal)).toFixed(4));
        }
      } catch (e) {
        console.error('Failed to fetch ETH balance:', e);
      }

      try {
        if (provider) {
          const addresses = await getDexAddresses();
          if (addresses?.usdc) {
            const usdcAbi = getAbi('dexUSDCABI') || ['function balanceOf(address) view returns (uint256)'];
            const usdcContract = new ethers.Contract(addresses.usdc, usdcAbi, provider);
            const usdcBal = await usdcContract.balanceOf(account);
            setUsdcBalance(parseFloat(ethers.formatUnits(usdcBal, 6)).toFixed(2));
          }
        }
      } catch (e) {
        console.error('Failed to fetch USDC balance:', e);
      }
    };

    fetchBalances();
    const interval = setInterval(fetchBalances, 30000);
    return () => clearInterval(interval);
  }, [account, isConnected, balanceOf, formatTokenAmount, provider, getDexAddresses, getAbi]);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/swap', label: 'Swap' },
  ];

  const isActivePath = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 h-20 bg-[rgba(15,23,42,0.6)] backdrop-blur-[16px] border-b border-white/5 px-4 sm:px-6 lg:px-8 flex items-center">
      <div className="max-w-7xl mx-auto w-full flex items-center justify-between">
        {/* Left: Logo */}
        {!minimal ? (
          <Link to="/" className="flex items-center gap-3 group shrink-0">
            <div className="w-8 h-8 bg-accent/20 border border-accent/30 rounded-xl flex items-center justify-center">
              <span className="text-accent font-bold text-lg font-display">S</span>
            </div>
            <span className="text-xl font-bold font-display text-white group-hover:text-accent transition-colors hidden sm:inline">
              Subscrypts
            </span>
          </Link>
        ) : (
          <div className="w-8" />
        )}

        {/* Center: Navigation (desktop only) */}
        {!minimal && (
          <nav className="hidden md:flex items-center gap-1 absolute left-1/2 -translate-x-1/2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  isActivePath(link.path)
                    ? 'bg-accent/10 text-accent border border-accent/20'
                    : 'text-text-muted hover:text-white hover:bg-white/5 border border-transparent'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>
        )}

        {/* Right: Balances + Wallet + Info + Menu */}
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          {/* Token Balances (desktop) */}
          {isConnected && (
            <div className="hidden lg:flex items-center gap-3 px-3 py-1.5 rounded-full bg-white/5 border border-white/5">
              <div className="flex items-center gap-1">
                <span className="text-accent font-medium text-sm">
                  {parseFloat(subsBalance).toFixed(2)}
                </span>
                <span className="text-text-muted text-xs">SUBS</span>
              </div>
              <div className="w-px h-4 bg-white/10" />
              <div className="flex items-center gap-1">
                <span className="text-blue-400 font-medium text-sm">
                  {parseFloat(usdcBalance).toFixed(2)}
                </span>
                <span className="text-text-muted text-xs">USDC</span>
              </div>
              <div className="w-px h-4 bg-white/10" />
              <div className="flex items-center gap-1">
                <span className="text-purple-400 font-medium text-sm">
                  {parseFloat(ethBalance).toFixed(4)}
                </span>
                <span className="text-text-muted text-xs">ETH</span>
              </div>
            </div>
          )}

          {/* Wallet Button */}
          <WalletButton />

          {/* Info Button */}
          {onInfoClick && (
            <button
              onClick={onInfoClick}
              className="p-2 rounded-full bg-white/5 border border-white/5 hover:bg-white/10 hover:border-white/10 transition-all text-text-muted hover:text-white"
              aria-label="Page information"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
          )}

          {/* Mobile Menu Button */}
          {!minimal && (
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
              aria-label="Toggle menu"
            >
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Mobile Navigation Panel */}
      {!minimal && isMenuOpen && (
        <div className="md:hidden absolute top-20 left-0 right-0 bg-[rgba(15,23,42,0.95)] backdrop-blur-[20px] border-b border-white/5 animate-fade-in">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <nav className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`px-4 py-3 rounded-2xl text-sm font-medium transition-colors ${
                    isActivePath(link.path)
                      ? 'bg-accent/10 text-accent border border-accent/20'
                      : 'text-text-muted hover:text-white hover:bg-white/5'
                  }`}
                >
                  {link.label}
                </Link>
              ))}

              {/* Mobile Balances */}
              {isConnected && (
                <div className="mt-2 px-4 py-3 rounded-2xl bg-white/5 border border-white/5 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-text-muted text-sm">SUBS</span>
                    <span className="text-accent font-medium">
                      {parseFloat(subsBalance).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-text-muted text-sm">USDC</span>
                    <span className="text-blue-400 font-medium">
                      {parseFloat(usdcBalance).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-text-muted text-sm">ETH</span>
                    <span className="text-purple-400 font-medium">
                      {parseFloat(ethBalance).toFixed(4)}
                    </span>
                  </div>
                </div>
              )}
            </nav>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
